import { ref, computed, watch } from 'vue';
import { fetchProvinces, fetchAllLocalities } from '@/services/api/provinceService';
import { fetchRegionals } from '@/services/api/regionalService';
import { fetchAllBrands } from '@/services/api/brandService';
import { vehicleTypes } from '@/services/api/vehicleTypes';
import { fetchEconomicGroupsList } from '@/services/api/economicGroupService';

export function useConcessionaireFilters() {
    const selectedProvince = ref([]);
    const selectedLocality = ref([]);
    const selectedRegional = ref([]);

    // filtros
    const selectedBrand = ref([]);
    const selectedSector = ref([]);
    const selectedEconomicGroup = ref([]);
    const withoutUsers = ref(false);
    const withoutAdministrators = ref(false);

    // Cache de API
    const allProvinces = ref([]);
    const allLocalities = ref([]);
    const allRegionals = ref([]);

    const brandsOptions = ref([]);
    const economicGroupsOptions = ref([]);

    // Loading
    const loadingProvinces = ref(true);
    const loadingLocalities = ref(true);
    const loadingRegionals = ref(true);
    const loadingBrands = ref(true);
    const loadingEconomicGroups = ref(true);

    const getIds = (value) => {
        if (!Array.isArray(value)) return [];
        if (value.length === 0) return [];
        return value.map(item => (typeof item === 'object' && item !== null) ? (item.id || item.value) : item);
    };

    const regionalsOptions = computed(() => allRegionals.value);

    const provincesOptions = computed(() => {
        const selectedRegIds = getIds(selectedRegional.value);

        if (selectedRegIds.length === 0) return allProvinces.value;

        const selectedRegionalObjects = allRegionals.value.filter(reg => selectedRegIds.includes(reg.id));

        const allowedProvinceIds = [...new Set(selectedRegionalObjects.map(reg => reg.province_id).filter(id => id))];

        return allProvinces.value.filter(prov => allowedProvinceIds.includes(prov.id));
    });

    const localitiesOptions = computed(() => {
        const provinceIds = getIds(selectedProvince.value);
        let filteredLocalities = [];

        if (provinceIds.length === 0) {
            filteredLocalities = allLocalities.value;
        } else {
            filteredLocalities = allLocalities.value.filter(locality =>
                locality.province_id && provinceIds.includes(locality.province_id)
            );
        }

        const groupedMap = filteredLocalities.reduce((groups, locality) => {
            const provinceName = locality.province_name || 'Sin Provincia';

            if (!groups[provinceName]) {
                groups[provinceName] = {
                    province_group_label: provinceName,
                    items: []
                };
            }
            groups[provinceName].items.push(locality);
            return groups;
        }, {});

        return Object.values(groupedMap).sort((a, b) =>
            a.province_group_label.localeCompare(b.province_group_label)
        );
    });

    watch(selectedRegional, () => {

        selectedProvince.value = [];
        selectedLocality.value = [];

        /* const regIds = getIds(newVal);
        if (regIds.length === 1) {
            const regionalObj = allRegionals.value.find(r => r.id === regIds[0]);
            if (regionalObj && regionalObj.province_id) {
                const provinceObj = allProvinces.value.find(p => p.id === regionalObj.province_id);
                if (provinceObj) {
                    selectedProvince.value = [provinceObj]; 
                }
            }
        }
        */
    });

    watch(selectedProvince, () => {
        selectedLocality.value = [];
    });

    const loadFilterOptions = async () => {
        try {
            loadingProvinces.value = true;
            loadingLocalities.value = true;
            loadingRegionals.value = true;
            loadingBrands.value = true;
            loadingEconomicGroups.value = true;

            const [provincesRes, localitiesRes, regionalsRes, brandsRes, groupsRes] = await Promise.all([
                fetchProvinces(),
                fetchAllLocalities(),
                fetchRegionals(),
                fetchAllBrands(),
                fetchEconomicGroupsList()
            ]);

            allProvinces.value = provincesRes.data || provincesRes;
            allLocalities.value = localitiesRes.data || localitiesRes;
            allRegionals.value = regionalsRes.data || regionalsRes;

            brandsOptions.value = brandsRes;
            economicGroupsOptions.value = groupsRes.data.data || groupsRes.data;

        } catch (error) {
            console.error('Error loading filter options:', error);
        } finally {
            loadingProvinces.value = false;
            loadingLocalities.value = false;
            loadingRegionals.value = false;
            loadingBrands.value = false;
            loadingEconomicGroups.value = false;
        }
    };

    const resetFilters = () => {
        selectedProvince.value = [];
        selectedLocality.value = [];
        selectedRegional.value = [];
        selectedBrand.value = [];
        selectedSector.value = [];
        selectedEconomicGroup.value = [];
        withoutUsers.value = false;
        withoutAdministrators.value = false;
    };

    return {
        selectedProvince,
        selectedLocality,
        selectedRegional,
        selectedBrand,
        selectedSector,
        selectedEconomicGroup,
        withoutUsers,
        withoutAdministrators,

        loadingProvinces,
        loadingLocalities,
        loadingRegionals,
        loadingBrands,
        loadingEconomicGroups,

        provincesOptions,
        localitiesOptions,
        regionalsOptions,

        brandsOptions: computed(() => brandsOptions.value),
        economicGroupsOptions: computed(() => economicGroupsOptions.value),
        sectorsOptions: computed(() => vehicleTypes),

        resetFilters,
        loadFilterOptions,

        activeFilters: computed(() => ({
            province_id: getIds(selectedProvince.value),
            locality_id: getIds(selectedLocality.value),
            regional_id: getIds(selectedRegional.value),
            brand_id: getIds(selectedBrand.value),
            sector: getIds(selectedSector.value),
            economic_group_id: getIds(selectedEconomicGroup.value),
            withoutUsers: withoutUsers.value ? 1 : 0,
            withoutAdministrators: withoutAdministrators.value ? 1 : 0,
        }))
    };
}